import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BookOpen, Heart, Users, Sparkles } from 'lucide-react';
import {
  isNotificationSupported,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPushNotifications,
} from '../utils/notifications';

const LandingPage = () => {
  const { login, API } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      login(response.data.token, response.data.user, response.data.has_security_questions);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerName,
        email: registerEmail,
        password: registerPassword
      });
      
      login(response.data.token, response.data.user, response.data.has_security_questions);
      toast.success('Account created successfully!');

      // Auto-enable push notifications right after signup so members
      // get devotionals, prayer updates, and daily reflections by default.
      // Silently no-op on iOS-without-PWA and on permission denial — the user
      // can always enable/tweak in Settings.
      try {
        if (isNotificationSupported()) {
          const granted = await requestNotificationPermission();
          if (granted) {
            await registerServiceWorker();
            await subscribeToPushNotifications(API, response.data.token);
            toast.success('Notifications enabled — you can turn any off in Settings.', {
              duration: 5000,
            });
          }
        }
      } catch (err) {
        console.warn('Auto-subscribe on signup failed:', err);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex justify-center mb-6">
            <img 
              src="/wfmc-logo.png" 
              alt="WFMC Logo" 
              className="h-32 w-auto"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F] mb-4">
            Discipleship Journey
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Track your spiritual journey at Westland Free Methodist Church
          </p>
        </div>

        {/* Auth Section — moved above vital signs grid */}
        <div className="max-w-md mx-auto mb-12">
          <Card className="glass shadow-xl" data-testid="auth-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Join Your Journey</CardTitle>
              <CardDescription>Begin tracking your spiritual growth today</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-11"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                      disabled={isLoading}
                      data-testid="login-submit-button"
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                    <div className="text-center">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-[#2B6B7F] hover:text-[#1E4D5F] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Input
                        type="text"
                        placeholder="Full Name"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        required
                        data-testid="register-name-input"
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        required
                        data-testid="register-email-input"
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        required
                        data-testid="register-password-input"
                        className="h-11"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                      disabled={isLoading}
                      data-testid="register-submit-button"
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>


        {/* Features Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-12 animate-slide-up">
          <Card className="card-hover border-t-4 border-t-[#2B6B7F]" data-testid="feature-pursue-god">
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-[#2B6B7F]" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-[#1E4D5F]">Pursue God</h3>
              <p className="text-sm text-slate-600">Communicate with God through prayer</p>
            </CardContent>
          </Card>

          <Card className="card-hover border-t-4 border-t-[#5FA8C1]" data-testid="feature-engage-scripture">
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#5FA8C1]/10 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-7 h-7 text-[#5FA8C1]" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-[#1E4D5F]">Engage Scripture</h3>
              <p className="text-sm text-slate-600">Active time in God's Word</p>
            </CardContent>
          </Card>

          <Card className="card-hover border-t-4 border-t-[#7BA888]" data-testid="feature-serve-others">
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#7BA888]/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-[#7BA888]" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-[#1E4D5F]">Serve Others</h3>
              <p className="text-sm text-slate-600">Love God and neighbors through service</p>
            </CardContent>
          </Card>

          <Card className="card-hover border-t-4 border-t-[#D4A574]" data-testid="feature-radiate-jesus">
            <CardContent className="pt-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#D4A574]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-[#D4A574]" />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-[#1E4D5F]">Radiate Jesus</h3>
              <p className="text-sm text-slate-600">Share the good news with others</p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-slate-600">
          <p className="text-sm">
            Westland Free Methodist Church - Path to Joy-Filled Wholeness
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
